import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Alternative, ParagraphFragment } from '../../graphql/fragment/paragraph.fragment'
import { TranscriptQueryDataTranscript } from '../../graphql/query/transcript.query'
import { equalId, ID } from '../../utils/id'

export type Tags = {
  [key: ID]: {
    id: ID
    name: string
    type: string | null
    created: string
    updated: string
  }
}

export type Speakers = {
  [key: ID]: {
    id: ID
    name: string
  }
}

export type Paragraphs = {
  [key: ID]: {
    id: ID
    checked: boolean
    visible: boolean
    startTime: number
    endTime: number
    speakerId: ID
    meetingAgendaId?: ID | null
    text: string
    tagIds: ID[]
    sentiment: number | null
    itemStartTimes: number [] | null
    itemEndTimes: number [] | null
    confidences: number [] | null
    alternatives: Alternative[]


    // extra options
    splitting: boolean
  }
}

export type HighlightWordRange = {
  startWord: number
  endWord: number
}

export type HighlightsMap = { [key: ID]: HighlightWordRange[] }


// TODO continue on transcripts
export type Transcript = {
  id: ID
  paragraphIds: ID[]
  tagIds: ID[]
  speakerIds: ID[]
}
export type Transcripts = {
  [key: ID]: Transcript
}

export type TranscriptState = {
  paragraphs: Paragraphs
  speakers: Speakers
  tags: Tags
  transcripts: Transcripts
  highlights: HighlightsMap
  visibleParagraphs: ID[]
}

const initialState: TranscriptState = {
  highlights: {},
  paragraphs: {},
  speakers: {},
  tags: {},
  transcripts: {},
  visibleParagraphs: [],
}

export const transcriptSlice = createSlice({
  initialState,

  name: 'transcript',

  reducers: {
    addParagraph: (
      state,
      action: PayloadAction<{paragraphId: ID; transcriptId: ID; paragraph: ParagraphFragment}>,
    ) => {
      action.payload.paragraph.tags.forEach((tag) => {
        state.tags[tag.id] = {
          created: tag.created,
          id: tag.id,
          name: tag.name,
          type: tag.type,
          updated: tag.updated,
        }
      })

      state.paragraphs[action.payload.paragraphId] = paragraphFragmentToImmutable(
        action.payload.paragraph,
      )

      state.transcripts[action.payload.transcriptId].paragraphIds.push(action.payload.paragraphId)

      return state
    },
    addSpeaker: (
      state,
      action: PayloadAction<{speakerId: ID; name: string; transcriptId: ID}>,
    ) => {
      state.transcripts[action.payload.transcriptId].speakerIds.push(
        action.payload.speakerId,
      )

      state.speakers[action.payload.speakerId] = {
        id: action.payload.speakerId,
        name: action.payload.name,
      }
      return state
    },
    addToHighlights: (
      state,
      action: PayloadAction<{id: ID; highlightedItem: HighlightWordRange}>,
    ) => {
      if (!state.highlights[action.payload.id]) {
        state.highlights[action.payload.id] = []
      }

      state.highlights[action.payload.id].push(
        action.payload.highlightedItem,
      )
      return state
    },
    addToVisibleParagraphs: (state, action: PayloadAction<ID>) => {
      if (!state.visibleParagraphs.includes(action.payload)) {
        state.visibleParagraphs.push(action.payload)
      }

      return state
    },
    changeParagraphChecked: (
      state,
      action: PayloadAction<{ paragraphId: ID; checked: boolean }>,
    ) => {
      state.paragraphs[action.payload.paragraphId].checked = action.payload.checked
      return state
    },
    changeParagraphSpeaker: (
      state,
      action: PayloadAction<{ paragraphId: ID; speakerId: ID }>,
    ) => {
      state.paragraphs[action.payload.paragraphId].speakerId = action.payload.speakerId
      return state
    },
    changeParagraphVisibility: (
      state,
      action: PayloadAction<{ paragraphId: ID; visible: boolean }>,
    ) => {
      state.paragraphs[action.payload.paragraphId].visible = action.payload.visible
      return state
    },
    changeSpeaker: (
      state,
      action: PayloadAction<{speakerId: ID; paragraphId: ID}>,
    ) => {
      state.paragraphs[action.payload.paragraphId].speakerId = action.payload.speakerId
      return state
    },
    deleteParagraph: (state, action: PayloadAction<{transcriptId: ID; id: ID}>) => {
      state.transcripts[action.payload.transcriptId].paragraphIds = state
        .transcripts[action.payload.transcriptId]
        .paragraphIds
        .filter(paragraphId => !equalId(paragraphId, action.payload.id))
      return state
    },
    editParagraph: (
      state,
      action: PayloadAction<{paragraphId: ID; paragraph: ParagraphFragment}>,
    ) => {
      action.payload.paragraph.tags.forEach((tag) => {
        state.tags[tag.id] = {
          created: tag.created,
          id: tag.id,
          name: tag.name,
          type: tag.type,
          updated: tag.updated,
        }
      })

      state.paragraphs[action.payload.paragraphId] = paragraphFragmentToImmutable(
        action.payload.paragraph,
      )

      return state
    },
    editSentiment: (
      state,
      action: PayloadAction<{paragraphId: ID; sentiment: number}>,
    ) => {
      state.paragraphs[action.payload.paragraphId].sentiment = action.payload.sentiment
      return state
    },
    editSpeaker: (state, action: PayloadAction<{ speakerId: ID; name: string }>) => {
      state.speakers[action.payload.speakerId].name = action.payload.name
      return state
    },
    editSplitting: (
      state,
      action: PayloadAction<{paragraphId: ID; splitting: boolean}>,
    ) => {
      state.paragraphs[action.payload.paragraphId].splitting = action.payload.splitting
      return state
    },
    removeFromHighlights: (
      state,
      action: PayloadAction<{id: ID; highlightedItem: HighlightWordRange}>,
    ) => {
      if (state.highlights[action.payload.id]) {
        state.highlights[action.payload.id] = state
          .highlights[action.payload.id]
          .filter(item => (
            item.startWord !== action.payload.highlightedItem.endWord
            && item.endWord !== action.payload.highlightedItem.endWord
          ))
      }

      return state
    },
    removeFromVisibleParagraphs: (state, action: PayloadAction<ID>) => {
      state.visibleParagraphs = state.visibleParagraphs.filter(id => !equalId(id, action.payload))
      return state
    },
    replaceSpeaker: (
      state,
      action: PayloadAction<{ previousId: ID; nextId: ID }>,
    ) => {
      Object.keys(state.paragraphs).forEach(paragraphId => {
        if (state.paragraphs[paragraphId].speakerId === action.payload.previousId) {
          state.paragraphs[paragraphId].speakerId = action.payload.nextId
        }
      })
      return state
    },
    setTranscript: (state, action: PayloadAction<TranscriptQueryDataTranscript>) => {
      action.payload.tags.forEach((tag) => {
        state.tags[tag.id] = {
          created: tag.created,
          id: tag.id,
          name: tag.name,
          type: tag.type,
          updated: tag.updated,
        }
      })

      action.payload.speakers.forEach((speaker) => {
        state.speakers[speaker.id] = {
          id: speaker.id,
          name: speaker.name,
        }
      })

      action.payload.paragraphs.forEach((paragraph) => {
        paragraph.tags.forEach((tag) => {
          if (state.tags[tag.id]) {
            return
          }

          state.tags[tag.id] = {
            created: tag.created,
            id: tag.id,
            name: tag.name,
            type: tag.type,
            updated: tag.updated,
          }
        })

        state.paragraphs[paragraph.id] = paragraphFragmentToImmutable(paragraph)
      })

      state.transcripts[action.payload.id] = transcriptFragmentToImmutable(
        action.payload,
      )
    },
  },
})

export const {
  changeParagraphVisibility,
  changeParagraphChecked,
  changeParagraphSpeaker,
  replaceSpeaker,
  editSpeaker,
  addSpeaker,
  changeSpeaker,
  addParagraph,
  editParagraph,
  deleteParagraph,
  addToHighlights,
  editSplitting,
  removeFromHighlights,
  addToVisibleParagraphs,
  removeFromVisibleParagraphs,
  editSentiment,
  setTranscript,
} = transcriptSlice.actions

const paragraphFragmentToImmutable = (paragraph: ParagraphFragment) => {
  return {
    alternatives: paragraph.alternatives.map((x: any) => {
      const { __typename, ...data } = x
      return data
    }),
    checked: paragraph.checked,
    confidences: paragraph.confidences,
    endTime: paragraph.endTime,
    id: paragraph.id,
    itemEndTimes: paragraph.itemEndTimes,
    itemStartTimes: paragraph.itemStartTimes,
    meetingAgendaId: paragraph.meetingAgenda ? paragraph.meetingAgenda.id : null,
    sentiment: paragraph.sentiment,
    speakerId: String(paragraph.speaker.id),
    // extra options
    splitting: false,
    startTime: paragraph.startTime,
    tagIds: paragraph.tags.map((tag) => String(tag.id)),
    text: paragraph.text,
    visible: paragraph.visible,

  }
}

const transcriptFragmentToImmutable = (transcript: TranscriptQueryDataTranscript) => {
  return {
    id: transcript.id,
    paragraphIds: transcript.paragraphs.map(p => String(p.id)),
    speakerIds: transcript.speakers.map(s => String(s.id)),
    tagIds: transcript.tags.map(t => String(t.id)),
  }
}
